<template>
    <el-dialog
        title="Manage Phone Numbers"
        width="70%"
        append-to-body
        :visible.sync="dialogVisible"
        :before-close="handleClose"
        :close-on-click-modal="false"
    >
        <el-alert
            v-if="isAssignmentBlocked"
            type="warning"
            :closable="false"
            show-icon
            class="mb-3"
        >
            <template slot="title">
                <span class="break-word text-dark">
                    Numbers cannot be assigned while this Enhanced Branded Caller ID bundle is under review.
                    Assignment is only allowed when the bundle is in Draft/Approved/Rejected status.
                </span>
            </template>
        </el-alert>

        <el-tabs v-model="activeTab" @tab-click="handleTabChange">
            <!-- Unassigned Numbers Tab -->
            <el-tab-pane label="Unassigned Phone Numbers" name="unassigned">
                <div v-if="!loadingUnassigned && unassignedNumbers.length === 0" class="text-center p-4">
                    <p>No unassigned incoming numbers found.</p>
                </div>

                <div v-else>
                    <el-input
                        v-model="searchUnassigned"
                        placeholder="Search by phone number, SID, or line name..."
                        size="small"
                        clearable
                        prefix-icon="el-icon-search"
                        style="width: 280px; display: block; margin-left: auto; margin-bottom: 12px;"
                    />
                    <div class="mb-3 d-flex justify-content-between align-items-center">
                        <div>
                            <el-button
                                type="primary"
                                size="small"
                                @click="confirmAssignAll"
                                :loading="assigningAll"
                                :disabled="isAssignmentBlocked || assigning || assigningAll"
                            >
                                Assign All
                            </el-button>
                            <el-button
                                type="success"
                                size="small"
                                @click="confirmAssignSelected"
                                :loading="assigning"
                                :disabled="isAssignmentBlocked || assigning || selectedUnassignedNumbers.length === 0"
                                class="ml-2"
                            >
                                Assign Selected ({{ selectedUnassignedNumbers.length }})
                            </el-button>
                        </div>
                        <div>
                            <span class="text-muted">
                                Total: {{ paginationUnassigned.total }} unassigned number(s)
                            </span>
                        </div>
                    </div>

                    <el-table
                        :data="unassignedNumbers"
                        v-loading="loadingUnassigned || assigning || assigningAll"
                        @selection-change="handleUnassignedSelectionChange"
                        style="width: 100%"
                    >
                        <el-table-column
                            type="selection"
                            width="55"
                        />
                        <el-table-column prop="id" label="ID" width="80" />
                        <el-table-column prop="phone_number" label="Phone Number" width="150">
                            <template v-slot="scope">
                                {{ scope.row.phone_number | fixPhone }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="sid" label="SID" min-width="180" show-overflow-tooltip>
                            <template v-slot="scope">
                                {{ scope.row.sid || '-' }}
                            </template>
                        </el-table-column>
                        <el-table-column label="Line" min-width="150" show-overflow-tooltip>
                            <template v-slot="scope">
                                <span v-if="scope.row.campaign">
                                    {{ scope.row.campaign.name || `#${scope.row.campaign.id}` }}
                                </span>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="country" label="Country" width="90">
                            <template v-slot="scope">
                                {{ scope.row.country || '-' }}
                            </template>
                        </el-table-column>
                    </el-table>

                    <div class="d-flex justify-content-end mt-3">
                        <el-pagination
                            layout="sizes, prev, pager, next"
                            :current-page.sync="paginationUnassigned.page"
                            :page-sizes="[10, 20, 50, 100]"
                            :page-size.sync="paginationUnassigned.size"
                            :total="paginationUnassigned.total"
                            :disabled="loadingUnassigned || assigning || assigningAll"
                            @size-change="handleUnassignedSizeChange"
                            @current-change="handleUnassignedPageChange"
                        />
                    </div>
                </div>
            </el-tab-pane>

            <!-- Assigned Numbers Tab -->
            <el-tab-pane label="Assigned Phone Numbers" name="assigned">
                <div v-if="!loadingAssigned && assignedNumbers.length === 0" class="text-center p-4">
                    <p>No assigned incoming numbers found.</p>
                </div>

                <div v-else>
                    <el-input
                        v-model="searchAssigned"
                        placeholder="Search by phone number, SID, or line name..."
                        size="small"
                        clearable
                        prefix-icon="el-icon-search"
                        style="width: 280px; display: block; margin-left: auto; margin-bottom: 12px;"
                    />
                    <div class="mb-3 d-flex justify-content-between align-items-center">
                        <div>
                            <el-button
                                type="warning"
                                size="small"
                                @click="confirmUnassignAll"
                                :loading="unassigningAll"
                                :disabled="unassigning || unassigningAll"
                            >
                                Unassign All
                            </el-button>
                            <el-button
                                type="danger"
                                size="small"
                                @click="confirmUnassignSelected"
                                :loading="unassigning"
                                :disabled="unassigning || selectedAssignedNumbers.length === 0"
                                class="ml-2"
                            >
                                Unassign Selected ({{ selectedAssignedNumbers.length }})
                            </el-button>
                        </div>
                        <div>
                            <span class="text-muted">
                                Total: {{ paginationAssigned.total }} assigned number(s)
                            </span>
                        </div>
                    </div>

                    <el-table
                        :data="assignedNumbers"
                        v-loading="loadingAssigned || unassigning || unassigningAll"
                        @selection-change="handleAssignedSelectionChange"
                        style="width: 100%"
                    >
                        <el-table-column
                            type="selection"
                            width="55"
                        />
                        <el-table-column prop="id" label="ID" width="80" />
                        <el-table-column prop="phone_number" label="Phone Number" width="150">
                            <template v-slot="scope">
                                {{ scope.row.phone_number | fixPhone }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="sid" label="SID" min-width="180" show-overflow-tooltip>
                            <template v-slot="scope">
                                {{ scope.row.sid || '-' }}
                            </template>
                        </el-table-column>
                        <el-table-column label="Line" min-width="150" show-overflow-tooltip>
                            <template v-slot="scope">
                                <span v-if="scope.row.campaign">
                                    {{ scope.row.campaign.name || `#${scope.row.campaign.id}` }}
                                </span>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="country" label="Country" width="90">
                            <template v-slot="scope">
                                {{ scope.row.country || '-' }}
                            </template>
                        </el-table-column>
                    </el-table>

                    <div class="d-flex justify-content-end mt-3">
                        <el-pagination
                            layout="sizes, prev, pager, next"
                            :current-page.sync="paginationAssigned.page"
                            :page-sizes="[10, 20, 50, 100]"
                            :page-size.sync="paginationAssigned.size"
                            :total="paginationAssigned.total"
                            :disabled="loadingAssigned || unassigning || unassigningAll"
                            @size-change="handleAssignedSizeChange"
                            @current-change="handleAssignedPageChange"
                        />
                    </div>
                </div>
            </el-tab-pane>
        </el-tabs>

        <div slot="footer" class="dialog-footer">
            <el-button @click="handleClose">Close</el-button>
        </div>
    </el-dialog>
</template>

<script>
import {TYPE_ENHANCED} from '../../constants/branded-caller-id-use-cases'
import {STATUS_PENDING, STATUS_IN_REVIEW} from '../../constants/compliance-bundle-statuses'

export default {
    name: 'UnregisteredNumbersDialog',
    props: {
        visible: { type: Boolean, default: false },
        bundle: { type: Object, required: false, default: null },
        bundleType: { type: Number, required: true },
        company: { type: Object, required: true },
    },
    data() {
        return {
            activeTab: 'unassigned',
            unassignedNumbers: [],
            assignedNumbers: [],
            selectedUnassignedNumbers: [],
            selectedAssignedNumbers: [],
            assigning: false,
            assigningAll: false,
            unassigning: false,
            unassigningAll: false,
            loadingUnassigned: false,
            loadingAssigned: false,
            paginationUnassigned: { page: 1, size: 10, total: 0 },
            paginationAssigned: { page: 1, size: 10, total: 0 },
            searchUnassigned: '',
            searchAssigned: '',
            searchUnassignedTimeout: null,
            searchAssignedTimeout: null,
        }
    },
    computed: {
        isAssignmentBlocked() {
            if (!this.bundle) {
                return false
            }
            const isEnhanced = this.bundle.compliance_registration?.type === TYPE_ENHANCED
            if (!isEnhanced) {
                return false
            }
            return [STATUS_PENDING, STATUS_IN_REVIEW].includes(this.bundle.status)
        },
        dialogVisible: {
            get() { return this.visible },
            set(value) { if (!value) this.handleClose() },
        },
    },
    watch: {
        searchUnassigned(val) {
            clearTimeout(this.searchUnassignedTimeout)
            if (val.length >= 3 || val.length === 0) {
                this.searchUnassignedTimeout = setTimeout(() => {
                    this.fetchUnassignedNumbers(1)
                }, 400)
            }
        },
        searchAssigned(val) {
            clearTimeout(this.searchAssignedTimeout)
            if (val.length >= 3 || val.length === 0) {
                this.searchAssignedTimeout = setTimeout(() => {
                    this.fetchAssignedNumbers(1)
                }, 400)
            }
        },
        visible(newValue) {
            if (newValue) {
                this.fetchUnassignedNumbers()
                this.fetchAssignedNumbers()
                this.setupEventListeners()
            } else {
                this.resetData()
                this.removeEventListeners()
            }
        },
    },
    mounted() {
        if (this.visible) {
            this.setupEventListeners()
        }
    },
    beforeDestroy() {
        this.removeEventListeners()
    },
    methods: {
        handleTabChange() {
            // Do nothing
        },

        fetchUnassignedNumbers(page = 1) {
            this.loadingUnassigned = true
            this.paginationUnassigned.page = page

            axios.get(`/api/admin/compliance/${this.company.id}/unregistered-incoming-numbers`, {
                params: {
                    page: this.paginationUnassigned.page,
                    size: this.paginationUnassigned.size,
                    bundle_type: this.bundleType,
                    search: this.searchUnassigned || undefined,
                },
            })
            .then((response) => {
                this.unassignedNumbers = response.data.data || []
                this.paginationUnassigned.total = response.data.total || 0
                this.paginationUnassigned.page = response.data.current_page || 1
            })
            .catch((err) => {
                this.$root.handleErrors(err)
            })
            .finally(() => {
                this.loadingUnassigned = false
            })
        },

        fetchAssignedNumbers(page = 1) {
            this.loadingAssigned = true
            this.paginationAssigned.page = page

            axios.get(`/api/admin/compliance/${this.company.id}/${this.bundleType}/${this.bundle.id}/assigned-incoming-numbers`, {
                params: {
                    page: this.paginationAssigned.page,
                    size: this.paginationAssigned.size,
                    search: this.searchAssigned || undefined,
                },
            })
            .then((response) => {
                this.assignedNumbers = response.data.data || []
                this.paginationAssigned.total = response.data.total || 0
                this.paginationAssigned.page = response.data.current_page || 1
            })
            .catch((err) => {
                this.$root.handleErrors(err)
            })
            .finally(() => {
                this.loadingAssigned = false
            })
        },

        handleUnassignedPageChange(page) {
            this.fetchUnassignedNumbers(page)
        },
        handleUnassignedSizeChange(size) {
            this.paginationUnassigned.size = size
            this.fetchUnassignedNumbers(1)
        },
        handleAssignedPageChange(page) {
            this.fetchAssignedNumbers(page)
        },
        handleAssignedSizeChange(size) {
            this.paginationAssigned.size = size
            this.fetchAssignedNumbers(1)
        },

        handleUnassignedSelectionChange(selection) {
            this.selectedUnassignedNumbers = selection.map((item) => item.id)
        },
        handleAssignedSelectionChange(selection) {
            this.selectedAssignedNumbers = selection.map((item) => item.id)
        },

        confirmAssignAll() {
            this.$confirm('Are you sure you want to assign all unassigned numbers to this bundle?', 'Confirm Assignment', {
                confirmButtonText: 'Yes, Assign All',
                cancelButtonText: 'Cancel',
                type: 'warning',
            }).then(() => {
                this.assigningAll = true
                this.assignNumbers([])
            }).catch(() => {})
        },

        confirmAssignSelected() {
            this.$confirm(`Are you sure you want to assign ${this.selectedUnassignedNumbers.length} selected number(s) to this bundle?`, 'Confirm Assignment', {
                confirmButtonText: 'Yes, Assign Selected',
                cancelButtonText: 'Cancel',
                type: 'warning',
            }).then(() => {
                this.assignNumbers(this.selectedUnassignedNumbers)
            }).catch(() => {})
        },

        confirmUnassignAll() {
            this.$confirm('Are you sure you want to unassign all assigned numbers from this bundle?', 'Confirm Unassignment', {
                confirmButtonText: 'Yes, Unassign All',
                cancelButtonText: 'Cancel',
                type: 'warning',
            }).then(() => {
                this.unassigningAll = true
                this.unassignNumbers([])
            }).catch(() => {})
        },

        confirmUnassignSelected() {
            this.$confirm(`Are you sure you want to unassign ${this.selectedAssignedNumbers.length} selected number(s) from this bundle?`, 'Confirm Unassignment', {
                confirmButtonText: 'Yes, Unassign Selected',
                cancelButtonText: 'Cancel',
                type: 'warning',
            }).then(() => {
                this.unassignNumbers(this.selectedAssignedNumbers)
            }).catch(() => {})
        },

        assignNumbers(incomingNumberIds) {
            this.assigning = true

            axios.post(
                `/api/admin/compliance/${this.company.id}/${this.bundleType}/${this.bundle.id}/assign-incoming-numbers`,
                { incoming_number_ids: incomingNumberIds }
            )
            .then((response) => {
                if (response.data.queued) {
                    this.$notify({
                        title: 'Queued',
                        message: response.data.message || 'Assignment has been queued.',
                        type: 'info',
                        showClose: true,
                    })
                }
            })
            .catch((err) => {
                this.$root.handleErrors(err)
                this.assigningAll = false
                this.assigning = false
            })
        },

        unassignNumbers(incomingNumberIds) {
            this.unassigning = true

            axios.post(
                `/api/admin/compliance/${this.company.id}/${this.bundleType}/${this.bundle.id}/unassign-incoming-numbers`,
                { incoming_number_ids: incomingNumberIds }
            )
            .then((response) => {
                if (response.data.queued) {
                    this.$notify({
                        title: 'Queued',
                        message: response.data.message || 'Unassignment has been queued.',
                        type: 'info',
                        showClose: true,
                    })
                }
            })
            .catch((err) => {
                this.$root.handleErrors(err)
                this.unassigningAll = false
                this.unassigning = false
            })
        },

        setupEventListeners() {
            VueEvent.listen('COMPLIANCE_BUNDLE_OPERATION_DONE', this.handleComplianceOperationDone)
        },
        removeEventListeners() {
            VueEvent.unlisten('COMPLIANCE_BUNDLE_OPERATION_DONE', this.handleComplianceOperationDone)
        },

        handleComplianceOperationDone(data) {
            if (+data.company_id !== +this.company.id || +data.bundle_type !== +this.bundleType) {
                return
            }

            if (data.operation === 'Assign Numbers') {
                this.assigningAll = false
                this.assigning = false
                this.$notify({
                    title: 'Success',
                    message: data.status === 'success' ? 'Numbers assigned successfully.' : 'Assignment completed with errors.',
                    type: data.status === 'success' ? 'success' : 'warning',
                    showClose: true,
                })
                this.fetchUnassignedNumbers(this.paginationUnassigned.page)
                this.fetchAssignedNumbers(this.paginationAssigned.page)
                this.$emit('assigned')
            } else if (data.operation === 'Unassign Numbers') {
                this.unassigningAll = false
                this.unassigning = false
                this.$notify({
                    title: 'Success',
                    message: data.status === 'success' ? 'Numbers unassigned successfully.' : 'Unassignment completed with errors.',
                    type: data.status === 'success' ? 'success' : 'warning',
                    showClose: true,
                })
                this.fetchUnassignedNumbers(this.paginationUnassigned.page)
                this.fetchAssignedNumbers(this.paginationAssigned.page)
                this.$emit('unassigned')
            }
        },

        resetData() {
            this.activeTab = 'unassigned'
            this.selectedUnassignedNumbers = []
            this.selectedAssignedNumbers = []
            this.unassignedNumbers = []
            this.assignedNumbers = []
            this.paginationUnassigned.page = 1
            this.paginationAssigned.page = 1
            this.searchUnassigned = ''
            this.searchAssigned = ''
        },

        handleClose() {
            this.resetData()
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
.dialog-footer {
    text-align: right;
}
</style>